import * as React from "react";
import { Box, Typography, useColorScheme, useTheme, Tooltip } from "@mui/material";
import { TimeSlot, mergeAdjacentSlots, timeToMinutes, minutesToTime } from "./types";


interface ScheduleGridProps {
  value?: TimeSlot[];
  onChange?: (slots: TimeSlot[]) => void;
  disabled?: boolean;
}

const DAYS = [
  { id: 1, name: "Mon", fullName: "Monday" },
  { id: 2, name: "Tue", fullName: "Tuesday" },
  { id: 3, name: "Wed", fullName: "Wednesday" },
  { id: 4, name: "Thu", fullName: "Thursday" },
  { id: 5, name: "Fri", fullName: "Friday" },
  { id: 6, name: "Sat", fullName: "Saturday" },
  { id: 7, name: "Sun", fullName: "Sunday" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const DAY_LABEL_WIDTH = 60;
const HOUR_COLUMN_WIDTH = 40;
const TOTAL_WIDTH = DAY_LABEL_WIDTH + HOURS.length * HOUR_COLUMN_WIDTH; // 60 + 24 * 40 = 1020px

const formatSlotText = (slot: TimeSlot) => {
  const startMinutes = timeToMinutes(slot.startTime);
  const endMinutes = timeToMinutes(slot.endTime);
  const duration = endMinutes - startMinutes;
  
  // Don't show text for half-hour slots
  if (duration < 60) {
    return '';
  }
  
  // For longer slots, show full format
  return `${slot.startTime} - ${slot.endTime}`;
};

const getSlotTooltipText = (slot: TimeSlot) => {
  return `${slot.startTime} - ${slot.endTime}`;
};

export function ScheduleGrid({ value = [], onChange, disabled = false }: ScheduleGridProps) {
  const theme = useTheme();
  const { colorScheme = "light" } = useColorScheme();
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState<{ day: number; hour: number; isSecondHalf: boolean } | null>(null);
  const [dragEnd, setDragEnd] = React.useState<{ day: number; hour: number; isSecondHalf: boolean } | null>(null);

  const mergedSlots = React.useMemo(() => mergeAdjacentSlots(value), [value]);

  const handleMouseDown = (day: number, hour: number, event: React.MouseEvent) => {
    if (disabled) return;
    
    // Determine if it's first or second half of the hour based on click position
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const isSecondHalf = x >= HOUR_COLUMN_WIDTH / 2;
    
    setIsDragging(true);
    setDragStart({ day, hour, isSecondHalf });
    setDragEnd({ day, hour, isSecondHalf });
  };

  const handleCellClick = (day: number, hour: number, event: React.MouseEvent) => {
    if (disabled || !onChange) return;
    
    // Prevent click if we're dragging
    if (isDragging) return;
    
    // Determine if it's first or second half of the hour based on click position
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const isSecondHalf = x >= HOUR_COLUMN_WIDTH / 2;
    
    let startTime: string;
    let endTime: string;
    
    if (isSecondHalf) {
      // Second half: 30 minutes to 60 minutes
      startTime = minutesToTime(hour * 60 + 30);
      endTime = hour === 23 ? "24:00" : minutesToTime((hour + 1) * 60);
    } else {
      // First half: 0 minutes to 30 minutes
      startTime = minutesToTime(hour * 60);
      endTime = minutesToTime(hour * 60 + 30);
    }
    
    const newSlot: TimeSlot = { day, startTime, endTime };
    const updatedSlots = mergeAdjacentSlots([...value, newSlot]);
    onChange(updatedSlots);
  };

  const handleMouseUp = React.useCallback(() => {
    if (!isDragging || !dragStart || !dragEnd || !onChange) {
      setIsDragging(false);
      setDragStart(null);
      setDragEnd(null);
      return;
    }

    const startDay = Math.min(dragStart.day, dragEnd.day);
    const endDay = Math.max(dragStart.day, dragEnd.day);
    
    // Calculate start and end times based on half-hour positions
    // Need to determine which is actually the start and end based on drag direction
    const startMinutes = Math.min(
      dragStart.hour * 60 + (dragStart.isSecondHalf ? 30 : 0),
      dragEnd.hour * 60 + (dragEnd.isSecondHalf ? 30 : 0)
    );
    const endMinutes = Math.max(
      dragStart.hour * 60 + (dragStart.isSecondHalf ? 60 : 30),
      dragEnd.hour * 60 + (dragEnd.isSecondHalf ? 60 : 30)
    );

    const newSlots: TimeSlot[] = [];

    // Create slots for each day in the range (allow overlaps; we'll merge after)
    for (let day = startDay; day <= endDay; day++) {
      const startTime = minutesToTime(startMinutes);
      const endTime = endMinutes >= 1440 ? "24:00" : minutesToTime(endMinutes);
      newSlots.push({ day, startTime, endTime });
    }

    if (newSlots.length > 0) {
      const updatedSlots = mergeAdjacentSlots([...value, ...newSlots]);
      onChange(updatedSlots);
    }

    setIsDragging(false);
    setDragStart(null);
    setDragEnd(null);
  }, [isDragging, dragStart, dragEnd, onChange, value]);

  React.useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isDragging || !dragStart) return;
      
      // Find the grid container
      const gridContainer = document.querySelector('[data-schedule-grid]') as HTMLElement;
      if (!gridContainer) return;
      
      const rect = gridContainer.getBoundingClientRect();
      const scrollLeft = gridContainer.scrollLeft || 0;
      const scrollTop = gridContainer.scrollTop || 0;
      
      const x = e.clientX - rect.left + scrollLeft - DAY_LABEL_WIDTH; // Use constant
      const y = e.clientY - rect.top + scrollTop - 40; // Subtract header height
      
      const hour = Math.floor(x / HOUR_COLUMN_WIDTH); // Use constant
      const day = Math.floor(y / 60) + 1;
      
      // Determine if it's first or second half of the hour based on position within the cell
      const cellOffset = x % HOUR_COLUMN_WIDTH;
      const isSecondHalf = cellOffset >= HOUR_COLUMN_WIDTH / 2;
      
      if (hour >= 0 && hour < 24 && day >= 1 && day <= 7) {
        setDragEnd({ day, hour, isSecondHalf });
      }
    };

    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleMouseUp();
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, dragStart, handleMouseUp]);

  const handleSlotClick = (slot: TimeSlot) => {
    if (disabled) return;
    
    // Remove ALL underlying slots on the same day that overlap the clicked (merged) slot
    const clickedStart = timeToMinutes(slot.startTime);
    const clickedEnd = timeToMinutes(slot.endTime);

    const updatedSlots = value.filter((s) => {
      if (s.day !== slot.day) return true;
      const start = timeToMinutes(s.startTime);
      const end = timeToMinutes(s.endTime);
      // keep only those that do NOT overlap with the clicked merged slot
      return (end <= clickedStart) || (start >= clickedEnd);
    });

    onChange?.(updatedSlots);
  };

  const getSlotStyle = (slot: TimeSlot) => {
    const startMinutes = timeToMinutes(slot.startTime);
    const endMinutes = timeToMinutes(slot.endTime);
    
    const left = (startMinutes / 60) * HOUR_COLUMN_WIDTH + DAY_LABEL_WIDTH; // Use constants
    const width = ((endMinutes - startMinutes) / 60) * HOUR_COLUMN_WIDTH;
    const top = (slot.day - 1) * 60 + 40; // 60px per day + 40px for header

    return {
      position: 'absolute' as const,
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: '50px', // Same height as day row
      backgroundColor: theme.palette.primary.light,
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start', // Align text to left edge
      paddingLeft: '4px', // Small padding from left edge
      fontSize: '12px',
      color: theme.palette.primary.contrastText,
      fontWeight: 500,
      zIndex: 2,
      lineHeight: 'normal',
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
      },
      userSelect: 'none',
      overflow: 'hidden',
    };
  };

  const getDragPreviewStyle = () => {
    if (!dragStart || !dragEnd) return {};

    const startDay = Math.min(dragStart.day, dragEnd.day);
    const endDay = Math.max(dragStart.day, dragEnd.day);
    
    // Calculate start and end positions based on half-hour positions
    // Need to determine which is actually the start and end based on drag direction
    const startMinutes = Math.min(
      dragStart.hour * 60 + (dragStart.isSecondHalf ? 30 : 0),
      dragEnd.hour * 60 + (dragEnd.isSecondHalf ? 30 : 0)
    );
    const endMinutes = Math.max(
      dragStart.hour * 60 + (dragStart.isSecondHalf ? 60 : 30),
      dragEnd.hour * 60 + (dragEnd.isSecondHalf ? 60 : 30)
    );
    
    const left = (startMinutes / 60) * HOUR_COLUMN_WIDTH + DAY_LABEL_WIDTH;
    const width = ((endMinutes - startMinutes) / 60) * HOUR_COLUMN_WIDTH;
    const top = (startDay - 1) * 60 + 40; // 60px per day + 40px for header
    const height = (endDay - startDay + 1) * 60; // Full height of selected rows

    return {
      position: 'absolute' as const,
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
      backgroundColor: 'var(--mui-palette-action-hover)',
      border: `2px dashed`,
      borderColor: colorScheme === "light" ? theme.palette.primary.main : 'white',
      borderRadius: '4px',
      pointerEvents: 'none' as const,
      zIndex: 3,
    };
  };

  return (
    <Box
      data-schedule-grid
      sx={{
        position: 'relative',
        border: `1px solid`,
        borderColor: colorScheme === "light" ? theme.palette.divider : 'gray',
        borderRadius: 1,
        width: '100%',
        maxWidth: 'max-content',
        height: 'max-content',
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: theme.palette.grey[100],
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.grey[400],
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: theme.palette.grey[600],
          },
        },
      }}
    >
      {/* Header with hours */}
      <Box
        sx={{
          display: 'flex',
          height: '40px',
          backgroundColor: colorScheme === "light" ? theme.palette.grey[100] : 'var(--mui-palette-divider)',
          borderBottom: `1px solid`,
          borderColor: colorScheme === "light" ? theme.palette.divider : 'gray',
          width: `${TOTAL_WIDTH}px`, // Exact width calculation
          position: 'sticky',
          top: 0,
          zIndex: 10, // Higher z-index to stay above content
        }}
      >
        <Box sx={{ width: `${DAY_LABEL_WIDTH}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: `1px solid`, borderColor: colorScheme === "light" ? theme.palette.divider : 'gray' }}>
          <Typography variant="caption" >
            Time
          </Typography>
        </Box>
        {HOURS.map(hour => (
          <Box
            key={hour}
            sx={{
              width: `${HOUR_COLUMN_WIDTH}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRight: `1px solid`,
              borderColor: colorScheme === "light" ? theme.palette.divider : 'gray',

              '&:last-child': {
                borderRight: 'none',
              },
            }}
          >
            <Typography variant="caption" sx={{ userSelect: 'none' }}>
              {hour}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Days and grid */}
      {DAYS.map(day => (
        <Box
          key={day.id}
          sx={{
            display: 'flex',
            height: '60px',
            borderBottom: day.id === 7 ? 'none' : `1px solid`,
            borderColor: colorScheme === "light" ? theme.palette.divider : 'gray',
            position: 'relative',
            width: `${TOTAL_WIDTH}px`, // Exact width calculation
          }}
        >
          {/* Day label */}
          <Box
            sx={{
              width: `${DAY_LABEL_WIDTH}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: colorScheme === "light" ? theme.palette.grey[50] : 'var(--mui-palette-divider)',
              borderRight: `1px solid`,
              borderColor: colorScheme === "light" ? theme.palette.divider : 'gray',

              '&:last-child': {
                borderRight: 'none',
              },
           
            }}
          >
            <Typography variant="body2" fontWeight={500} sx={{ userSelect: 'none' }}>
              {day.name}
            </Typography>
          </Box>

          {/* Hour columns */}
          {HOURS.map(hour => (
            <Box
              key={hour}
              sx={{
                width: `${HOUR_COLUMN_WIDTH}px`,
                height: '100%',
                borderRight: `1px solid`,
                borderColor: colorScheme === "light" ? theme.palette.divider : 'gray',
                '&:last-child': {
                  borderRight: 'none',
                },
                cursor: disabled ? 'not-allowed' : 'crosshair',
                backgroundColor: 'transparent',
                opacity: 1,
                position: 'relative',
                '&:hover': {
                  backgroundColor: disabled ? 'transparent' : theme.palette.action.hover,
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  left: '50%',
                  top: 0,
                  bottom: 0,
                  width: '1px',
                  backgroundColor: theme.palette.divider,
                  opacity: 0.3,
                  pointerEvents: 'none',
                },
              }}
              onMouseDown={(e) => handleMouseDown(day.id, hour, e)}
              onClick={(e) => {
                e.preventDefault();
                handleCellClick(day.id, hour, e);
              }}
            />
          ))}
        </Box>
      ))}

      {/* Existing slots */}
      {mergedSlots.map((slot, index) => {
        const startMinutes = timeToMinutes(slot.startTime);
        const endMinutes = timeToMinutes(slot.endTime);
        const duration = endMinutes - startMinutes;
        const isHalfHour = duration < 60;
        
        const slotElement = (
          <Box
            sx={getSlotStyle(slot)}
            onClick={() => handleSlotClick(slot)}
          >
            {formatSlotText(slot)}
          </Box>
        );
        
        return isHalfHour ? (
          <Tooltip key={`${slot.day}-${slot.startTime}-${slot.endTime}-${index}`} title={getSlotTooltipText(slot)}>
            {slotElement}
          </Tooltip>
        ) : (
          <React.Fragment key={`${slot.day}-${slot.startTime}-${slot.endTime}-${index}`}>
            {slotElement}
          </React.Fragment>
        );
      })}

      {/* Drag preview */}
      {isDragging && dragStart && dragEnd && (
        <Box sx={getDragPreviewStyle()} />
      )}
    </Box>
  );
}
